import React, { useEffect, useMemo, useState } from "react";
import { isAdmin, isPlatformAdmin } from "../utils/auth";
import api from "../api";
import { actualizarUsuario, crearUsuario as crearUsuarioApi, desactivarUsuario, getUserAccess, getUsuarios, updateUserAccess } from "../api";
import ModuleHeader from "./ui/ModuleHeader";
import { ModuleWorkspace } from "./workspace/ModuleWorkspace";
import EntityIndexItem from "./ui/EntityIndexItem";
import "./UsuariosTab.css";

export default function UsuariosTab() {
  const admin = isAdmin();
  const platformAdmin = isPlatformAdmin();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [nuevo, setNuevo] = useState({ display_name: "", email: "", password: "", rol: "tecnico" });
  const [creating, setCreating] = useState(false);

  const [editId, setEditId] = useState(null);
  const [edits, setEdits] = useState({}); // id -> { password, rol }
  const [savingId, setSavingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const [whatsappSaving, setWhatsappSaving] = useState(false);
  const [whatsappEdits, setWhatsappEdits] = useState({});

  const [accessData, setAccessData] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);

  const cargarUsuarios = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await getUsuarios();
      const list = Array.isArray(res.data) ? res.data : [];
      setUsuarios(list);
      setSelectedId((current) => list.some((item) => String(item.id) === String(current)) ? current : list[0]?.id || null);
    } catch (e) {
      console.error("[UsuariosTab] get /api/usuarios", e);
      setErr(e?.response?.data?.error || "No se pudieron cargar los usuarios.");
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const crearUsuario = async () => {
    const displayName = String(nuevo.display_name || "").trim();
    const email = String(nuevo.email || "").trim().toLowerCase();
    const password = String(nuevo.password || "").trim();
    if (!displayName || !email || !password) return alert("Completa nombre, email y contraseña.");

    setCreating(true);
    try {
      await crearUsuarioApi({ ...nuevo, display_name: displayName, email, password });
      setNuevo({ display_name: "", email: "", password: "", rol: "tecnico" });
      await cargarUsuarios();
    } catch (e) {
      console.error("[UsuariosTab] post /api/usuarios", e);
      alert(e?.response?.data?.error || "No se pudo crear el usuario.");
    } finally {
      setCreating(false);
    }
  };

  const guardarWhatsApp = async (user) => {
    const allowed = platformAdmin || (
      admin &&
      user.rol !== "admin" &&
      !user.platform_admin
    );
    if (!allowed) return;

    const settings = whatsappEdits[user.id] || {
      telefono_whatsapp: user.telefono_whatsapp || "",
      whatsapp_gateway_autorizado: Boolean(user.whatsapp_gateway_autorizado)
    };

    setWhatsappSaving(true);
    try {
      await api.put(`/usuarios/${user.id}/whatsapp`, {
        telefono_whatsapp: settings.telefono_whatsapp,
        whatsapp_gateway_autorizado: Boolean(settings.whatsapp_gateway_autorizado)
      });
      setWhatsappEdits((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
      await cargarUsuarios();
    } catch (error) {
      alert(error?.response?.data?.error || "No se pudo guardar WhatsApp.");
    } finally {
      setWhatsappSaving(false);
    }
  };

  const startEdit = (u) => {
    setSelectedId(u.id);
    setEditId(u.id);
    setEdits((prev) => ({
      ...prev,
      [u.id]: {
        display_name: u.display_name || u.username || "",
        email: u.email || "",
        password: "",
        rol: u.rol || "tecnico",
        active: Boolean(u.active),
        treyder_employee_id: u.treyder_employee_id || '',
      },
    }));
  };

  const cancelEdit = (id) => {
    setEditId(null);
    setEdits((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const guardarCambios = async (id) => {
    const cambios = edits[id];
    if (!cambios) return;

    const payload = {
      display_name: String(cambios.display_name || "").trim(),
      email: String(cambios.email || "").trim().toLowerCase(),
      rol: cambios.rol,
      active: Boolean(cambios.active),
      treyder_employee_id: String(cambios.treyder_employee_id || '').trim() === '' ? null : Number(cambios.treyder_employee_id),
    };
    if (String(cambios.password || "").trim()) payload.password = String(cambios.password).trim();

    setSavingId(id);
    try {
      await actualizarUsuario(id, payload);
      cancelEdit(id);
      await cargarUsuarios();
    } catch (e) {
      console.error("[UsuariosTab] put /api/usuarios/:id", e);
      alert(e?.response?.data?.error || "No se pudieron guardar los cambios.");
    } finally {
      setSavingId(null);
    }
  };

  const cambiarEstadoUsuario = async (u) => {
    const nextActive = !u.active;
    if (!nextActive && !window.confirm("¿Desactivar este usuario? Perderá acceso inmediatamente.")) return;
    try {
      if (nextActive) {
        await actualizarUsuario(u.id, { active: true });
      } else {
        await desactivarUsuario(u.id);
      }
      await cargarUsuarios();
    } catch (e) {
      console.error("[UsuariosTab] change user status", e);
      alert(e?.response?.data?.error || "No se pudo cambiar el estado del usuario.");
    }
  };

  const usuariosSorted = useMemo(() => {
    return [...(Array.isArray(usuarios) ? usuarios : [])].sort((a, b) =>
      String(a?.display_name || a?.username || "").localeCompare(String(b?.display_name || b?.username || ""))
    );
  }, [usuarios]);

  const usuariosVisibles = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("es");
    if (!term) return usuariosSorted;
    return usuariosSorted.filter((user) => [user.display_name, user.username, user.email, user.rol].filter(Boolean).join(" ").toLocaleLowerCase("es").includes(term));
  }, [usuariosSorted, query]);
  const selectedUser = useMemo(() => usuariosSorted.find((user) => String(user.id) === String(selectedId)) || null, [usuariosSorted, selectedId]);

  const editarAccesos = async (userId) => {
    setSelectedId(userId);
    setAccessLoading(true);
    setErr("");
    try {
      const res = await getUserAccess(userId);
      setAccessData(res.data);
    } catch (e) {
      console.error("[UsuariosTab] get access-control", e);
      setErr(e?.response?.data?.error || "No se pudieron cargar los accesos.");
    } finally {
      setAccessLoading(false);
    }
  };

  const toggleModule = (moduleKey) => {
    setAccessData((prev) => ({
      ...prev,
      modules: prev.modules.map((module) =>
        module.module_key === moduleKey ? { ...module, assigned: !module.assigned } : module
      ),
    }));
  };

  const toggleClient = (clientId) => {
    setAccessData((prev) => ({
      ...prev,
      clients: prev.clients.map((client) =>
        client.id === clientId ? { ...client, assigned: !client.assigned } : client
      ),
    }));
  };

  const guardarAccesos = async () => {
    if (!accessData?.user?.id) return;
    setAccessSaving(true);
    try {
      await updateUserAccess(accessData.user.id, {
        module_keys: accessData.modules.filter((item) => item.assigned).map((item) => item.module_key),
        module_levels: Object.fromEntries(accessData.modules.filter((item) => item.assigned).map((item) => [item.module_key, item.access_level || "manage"])),
        all_clients: Boolean(accessData.all_clients),
        client_ids: accessData.clients.filter((item) => item.assigned).map((item) => item.id),
      });
      await editarAccesos(accessData.user.id);
    } catch (e) {
      console.error("[UsuariosTab] put access-control", e);
      alert(e?.response?.data?.error || "No se pudieron guardar los accesos.");
    } finally {
      setAccessSaving(false);
    }
  };

  const cambiarMfa=async(user, reset=false)=>{const required=reset?false:!Boolean(user.mfa_required);const message=reset?'¿Desactivar y reiniciar el 2FA de este usuario? Tendrá que volver a configurarlo.':required?'¿Solicitar 2FA a este usuario? Se le pedirá configurarlo al acceder.':'¿Quitar el requisito de 2FA a este usuario?';if(!window.confirm(message))return;try{await api.put('/usuarios/'+user.id+'/mfa',{required,reset});await cargarUsuarios();}catch(e){alert(e?.response?.data?.error||'No se pudo actualizar el 2FA');}};
  const roleLabel = (role) => role === "admin" ? "Administrador" : role === "supervisor" ? "Supervisor" : "Técnico";
  const selectedChanges = selectedUser ? edits[selectedUser.id] || {} : {};
  const selectedEditing = Boolean(selectedUser && editId === selectedUser.id);
  const canManageWhatsApp = Boolean(
    selectedUser &&
    (
      platformAdmin ||
      (
        admin &&
        selectedUser.rol !== "admin" &&
        !selectedUser.platform_admin
      )
    )
  );

  const selectedRole = selectedEditing ? selectedChanges.rol || selectedUser.rol : selectedUser?.rol || "tecnico";
  const selectedCanManage = Boolean(
    selectedUser &&
    (
      platformAdmin ||
      (
        admin &&
        selectedUser.rol !== "admin" &&
        !selectedUser.platform_admin
      ) ||
      (
        !admin &&
        selectedUser.rol === "tecnico" &&
        !selectedUser.platform_admin
      )
    )
  );
  const selectedPending = Boolean(selectedUser && selectedEditing && (selectedChanges.rol !== selectedUser.rol || selectedChanges.display_name !== (selectedUser.display_name || selectedUser.username || "") || selectedChanges.email !== (selectedUser.email || "") || selectedChanges.active !== Boolean(selectedUser.active) || String(selectedChanges.password || "").trim().length > 0));

  return (
    <section className="uTab">
      <ModuleWorkspace
        ariaLabel="Usuarios y roles"
        detailKey={selectedUser?.id || (showCreate ? "new" : "none")}
        header={<ModuleHeader eyebrow="Administración" title="Usuarios y roles" description="Gestión de técnicos, perfiles y acceso al sistema." actions={<button className="button secondary small" type="button" onClick={cargarUsuarios} disabled={loading}>{loading ? "Recargando…" : "↻ Recargar"}</button>} />}
        list={<aside className="card usersIndex" aria-label="Índice de usuarios"><header className="usersIndexHead"><div><strong>Usuarios</strong><span>{usuariosVisibles.length} visibles</span></div><button className="button primary small" type="button" onClick={() => { setShowCreate(true); setEditId(null); setAccessData(null); }}>＋ Nuevo</button></header><input className="input usersIndexSearch" type="search" placeholder="Buscar nombre, correo o rol…" value={query} onChange={(event) => setQuery(event.target.value)} /><div className="usersIndexResults">{loading ? <div className="mp-empty-state">Cargando usuarios…</div> : usuariosVisibles.length ? usuariosVisibles.map((user) => <EntityIndexItem key={user.id} selected={String(selectedUser?.id) === String(user.id) && !showCreate} onClick={() => { setSelectedId(user.id); setShowCreate(false); setEditId(null); setAccessData(null); }} title={user.display_name || user.username || "Usuario sin nombre"} subtitle={user.email || "Sin correo"} status={{ label: user.active ? "Activo" : "Inactivo", tone: user.active ? "ok" : "inactive" }} tags={[roleLabel(user.rol), ...(user.platform_admin ? ["Superadmin"] : [])]} />) : <div className="mp-empty-state"><strong>No hay usuarios con este filtro</strong><span>Crea un usuario o modifica la búsqueda.</span></div>}</div></aside>}
        detail={<>{err ? <div className="uError" role="alert">{err}</div> : null}{showCreate ? <section className="card mp-section uCard usersDetail"><header className="mp-section-head uCardHead"><div><div className="uH">Nuevo usuario</div><div className="uSub2">Crea una cuenta y asigna su rol inicial.</div></div><button className="button secondary small" type="button" onClick={() => setShowCreate(false)}>Cancelar</button></header><div className="uGrid"><div className="uField"><label>Nombre</label><input className="input" placeholder="Nombre y apellidos" value={nuevo.display_name} onChange={(e) => setNuevo((p) => ({ ...p, display_name: e.target.value }))} /></div><div className="uField"><label>Email</label><input className="input" type="email" placeholder="usuario@empresa.com" value={nuevo.email} onChange={(e) => setNuevo((p) => ({ ...p, email: e.target.value }))} /></div><div className="uField"><label>Contraseña</label><input className="input" placeholder="••••••••" type="password" value={nuevo.password} onChange={(e) => setNuevo((p) => ({ ...p, password: e.target.value }))} /></div><div className="uField"><label>Rol</label><select className="input" value={nuevo.rol} onChange={(e) => setNuevo((p) => ({ ...p, rol: e.target.value }))}><option value="tecnico">Técnico</option>{admin ? <option value="supervisor">Supervisor</option> : null}{platformAdmin ? <option value="admin">Administrador</option> : null}</select></div></div><footer className="usersDetailFooter"><button className="button primary" type="button" onClick={crearUsuario} disabled={creating}>{creating ? "Creando…" : "Crear usuario"}</button></footer></section> : selectedUser ? <section className="card mp-section uCard usersDetail"><header className="mp-section-head uCardHead usersDetailHead"><div><div className="usersDetailTitle"><h2>{selectedUser.display_name || selectedUser.username}</h2><span className={selectedUser.active ? "pill pill-ok" : "pill pill-neutral"}>{selectedUser.active ? "Activo" : "Inactivo"}</span></div><p>{selectedUser.email || "Sin correo"} · {roleLabel(selectedUser.rol)}{selectedUser.platform_admin ? " · Superadmin" : ""}</p></div><div className="usersDetailActions">{!selectedEditing ? <><button className="button secondary small" type="button" onClick={() => startEdit(selectedUser)} disabled={!selectedCanManage}>Editar</button><button className="button secondary small" type="button" onClick={() => cambiarMfa(selectedUser)} disabled={!selectedCanManage}>{selectedUser.mfa_required ? "2FA opcional" : "Solicitar 2FA"}</button>{selectedUser.mfa_enabled ? <button className="button danger small" type="button" onClick={() => cambiarMfa(selectedUser, true)} disabled={!selectedCanManage}>Reiniciar 2FA</button> : null}{(selectedUser.rol !== "admin" || platformAdmin) && selectedUser.active ? <button className="button secondary small" type="button" onClick={() => editarAccesos(selectedUser.id)} disabled={accessLoading || !selectedCanManage}>{accessLoading ? "Cargando…" : "Accesos"}</button> : null}<button className={selectedUser.active ? "button danger small" : "button secondary small"} type="button" onClick={() => cambiarEstadoUsuario(selectedUser)} disabled={!selectedCanManage}>{selectedUser.active ? "Desactivar" : "Reactivar"}</button></> : null}</div></header>{selectedEditing ? <><div className="uGrid"><div className="uField"><label>Nombre</label><input className="input" value={selectedChanges.display_name || ""} onChange={(e) => setEdits((prev) => ({ ...prev, [selectedUser.id]: { ...prev[selectedUser.id], display_name: e.target.value } }))} /></div><div className="uField"><label>Email</label><input className="input" type="email" value={selectedChanges.email || ""} onChange={(e) => setEdits((prev) => ({ ...prev, [selectedUser.id]: { ...prev[selectedUser.id], email: e.target.value } }))} /></div><div className="uField"><label>Nueva contraseña</label><input className="input" type="password" placeholder="Déjala vacía para conservarla" value={selectedChanges.password || ""} onChange={(e) => setEdits((prev) => ({ ...prev, [selectedUser.id]: { ...prev[selectedUser.id], password: e.target.value } }))} /></div><div className="uField"><label>Rol</label><select className="input" value={selectedRole} onChange={(e) => setEdits((prev) => ({ ...prev, [selectedUser.id]: { ...(prev[selectedUser.id] || {}), rol: e.target.value } }))}><option value="tecnico">Técnico</option>{admin ? <option value="supervisor">Supervisor</option> : null}{platformAdmin ? <option value="admin">Administrador</option> : null}</select></div><div className="uField"><label>ID empleado Treyder</label><input className="input" inputMode="numeric" min="1" type="number" placeholder="Sin asociar" value={selectedChanges.treyder_employee_id || ''} onChange={(e) => setEdits((prev) => ({ ...prev, [selectedUser.id]: { ...prev[selectedUser.id], treyder_employee_id: e.target.value } }))} /><small>Se usa al crear avisos en Treyder.</small></div><label className="uActiveField"><input type="checkbox" checked={Boolean(selectedChanges.active)} onChange={(e) => setEdits((prev) => ({ ...prev, [selectedUser.id]: { ...prev[selectedUser.id], active: e.target.checked } }))} /> Usuario activo</label></div><footer className="usersDetailFooter"><button className="button secondary" type="button" onClick={() => cancelEdit(selectedUser.id)}>Cancelar</button><button className="button primary" type="button" onClick={() => guardarCambios(selectedUser.id)} disabled={savingId === selectedUser.id || !selectedPending}>{savingId === selectedUser.id ? "Guardando…" : "Guardar cambios"}</button></footer></> : <dl className="usersDetailMeta"><div><dt>Rol</dt><dd>{roleLabel(selectedUser.rol)}</dd></div><div><dt>Estado</dt><dd>{selectedUser.active ? "Activo" : "Inactivo"}</dd></div><div><dt>Identificador</dt><dd>ID {selectedUser.id}</dd></div><div><dt>Ámbito</dt><dd>{selectedUser.platform_admin ? "Administración de plataforma" : "Cuenta de la organización"}</dd></div><div><dt>Doble factor</dt><dd>{selectedUser.mfa_enabled ? "Activo" : selectedUser.mfa_required ? "Pendiente de configurar" : "Opcional"}</dd></div><div><dt>Empleado Treyder</dt><dd>{selectedUser.treyder_employee_id ? "ID " + selectedUser.treyder_employee_id : "Sin asociar"}</dd></div></dl>}<section className="usersAccess">
<header>
  <div>
    <h3>WhatsApp de Mantenimientos</h3>
    <p>Autoriza el acceso técnico desde WhatsApp.</p>
  </div>
</header>
<div className="uGrid">
  <div className="uField">
    <label>Teléfono WhatsApp</label>
    <input
      className="input"
      type="tel"
      placeholder="+34 600 000 000"
      disabled={!canManageWhatsApp || whatsappSaving}
      value={(whatsappEdits[selectedUser.id]?.telefono_whatsapp ??
        selectedUser.telefono_whatsapp) || ""}
      onChange={(e) => setWhatsappEdits((prev) => ({
        ...prev,
        [selectedUser.id]: {
          telefono_whatsapp: e.target.value,
          whatsapp_gateway_autorizado:
            prev[selectedUser.id]?.whatsapp_gateway_autorizado ??
            Boolean(selectedUser.whatsapp_gateway_autorizado)
        }
      }))}
    />
    <small>Introduce el número con prefijo internacional.</small>
  </div>
  <label className="uActiveField">
    <input
      type="checkbox"
      disabled={!canManageWhatsApp || whatsappSaving || !selectedUser.active}
      checked={Boolean(
        whatsappEdits[selectedUser.id]?.whatsapp_gateway_autorizado ??
        selectedUser.whatsapp_gateway_autorizado
      )}
      onChange={(e) => setWhatsappEdits((prev) => ({
        ...prev,
        [selectedUser.id]: {
          telefono_whatsapp:
            prev[selectedUser.id]?.telefono_whatsapp ??
            selectedUser.telefono_whatsapp ?? "",
          whatsapp_gateway_autorizado: e.target.checked
        }
      }))}
    />
    Autorizar Gateway WhatsApp
  </label>
</div>
{canManageWhatsApp ? (
  <footer className="usersDetailFooter">
    <button
      className="button primary"
      type="button"
      disabled={whatsappSaving || !whatsappEdits[selectedUser.id]}
      onClick={() => guardarWhatsApp(selectedUser)}
    >
      {whatsappSaving ? "Guardando…" : "Guardar WhatsApp"}
    </button>
  </footer>
) : (
  <p>Tu perfil no permite modificar esta autorización.</p>
)}
</section>{accessData && String(accessData.user?.id) === String(selectedUser.id) ? <section className="usersAccess"><header><div><h3>Accesos de {accessData.user.username}</h3><p>Selecciona módulos y clientes autorizados.</p></div><div className="usersDetailActions"><button className="button secondary small" type="button" onClick={() => setAccessData(null)}>Cerrar</button><button className="button primary small" type="button" onClick={guardarAccesos} disabled={accessSaving}>{accessSaving ? "Guardando…" : "Guardar accesos"}</button></div></header><div className="uAccessGrid"><div className="uPermGroup"><div className="uPermGroupTitle">Módulos</div><div className="uPermList">{accessData.modules.map((module) => <label key={module.module_key} className="uPermItem"><input type="checkbox" checked={Boolean(module.assigned)} onChange={() => toggleModule(module.module_key)} /><span className="uPermLabel">{module.name}</span>{module.assigned ? <select className="input uPermissionLevel" aria-label={"Nivel de permiso para " + module.name} value={module.access_level || "manage"} onChange={(e) => setAccessData((prev) => ({ ...prev, modules: prev.modules.map((item) => item.module_key === module.module_key ? { ...item, access_level: e.target.value } : item) }))}><option value="read">Consulta</option><option value="manage">Gestionar</option></select> : null}</label>)}</div></div>{accessData.user.rol === "tecnico" ? <div className="uPermGroup"><div className="uPermGroupTitle">Clientes</div><label className="uPermItem uAllClients"><input type="checkbox" checked={Boolean(accessData.all_clients)} onChange={(e) => setAccessData((prev) => ({ ...prev, all_clients: e.target.checked }))} /><span className="uPermLabel">Todos los clientes</span></label><div className="uClientList">{accessData.clients.map((client) => <label key={client.id} className="uPermItem"><input type="checkbox" checked={Boolean(client.assigned)} disabled={Boolean(accessData.all_clients)} onChange={() => toggleClient(client.id)} /><span className="uPermLabel">{client.nombre}</span><span className="uPermKey">{client.codigo_cliente || "ID " + client.id}</span></label>)}</div></div> : null}</div></section> : null}</section> : <section className="card uCard usersEmpty"><strong>Selecciona un usuario</strong><span>Consulta su perfil, modifica su rol o ajusta sus accesos.</span></section>}</>}
      />
    </section>
  );
}
